/**
 * useSubmissionFilters Hook
 *
 * Encapsulates the logic for:
 * - Managing filter state via URL params (name, email, dates)
 * - Managing report status filter via Jotai
 * - Providing filter functions for submissions
 *
 * Follows Single Responsibility Principle - only handles filter logic
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtom, useSetAtom } from "jotai";
import { format } from "date-fns";
import {
  reportStatusFilterAtom,
  currentPageAtom,
  resetFiltersAtom,
} from "../store";
import type {
  SubmissionFilters,
  ReportStatusFilter,
  SubmissionItem,
  GeneratedReportsData,
} from "../store/types";
import { getSubmissionName, getSubmissionEmail } from "@/utils/submissionHelpers";

interface UseSubmissionFiltersResult {
  /** Current filter values */
  filters: SubmissionFilters;
  /** Update a specific filter */
  setFilter: (key: keyof SubmissionFilters, value: string) => void;
  /** Update date filter */
  setDateFilter: (key: "dateFrom" | "dateTo", date: Date | undefined) => void;
  /** Set report status filter */
  setReportStatus: (status: ReportStatusFilter) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Check if any filters are active */
  hasActiveFilters: boolean;
  /** Filter function to apply to submissions */
  filterSubmissions: (
    submissions: SubmissionItem[],
    launchCodes: string[] | null,
    reportsData: GeneratedReportsData | undefined
  ) => SubmissionItem[];
}

/**
 * Hook for managing submission filters with URL synchronization
 *
 * @example
 * ```tsx
 * const { filters, setFilter, filterSubmissions } = useSubmissionFilters();
 *
 * // Apply filters
 * const filtered = filterSubmissions(submissions, launchCodes, reportsData);
 * ```
 */
export function useSubmissionFilters(): UseSubmissionFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportStatus, setReportStatusAtom] = useAtom(reportStatusFilterAtom);
  const setCurrentPage = useSetAtom(currentPageAtom);
  const resetFilters = useSetAtom(resetFiltersAtom);

  // Extract filters from URL params
  const filters: SubmissionFilters = useMemo(
    () => ({
      name: searchParams.get("name") || "",
      email: searchParams.get("email") || "",
      dateFrom: searchParams.get("date_from") || "",
      dateTo: searchParams.get("date_to") || "",
      reportStatus,
    }),
    [searchParams, reportStatus]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () =>
      filters.name !== "" ||
      filters.email !== "" ||
      filters.dateFrom !== "" ||
      filters.dateTo !== "" ||
      filters.reportStatus !== "all",
    [filters]
  );

  // Update URL param filter
  const setFilter = useCallback(
    (key: keyof SubmissionFilters, value: string) => {
      const urlKey =
        key === "dateFrom"
          ? "date_from"
          : key === "dateTo"
            ? "date_to"
            : key === "reportStatus"
              ? null // reportStatus is not in URL
              : key;

      if (key === "reportStatus") {
        setReportStatusAtom(value as ReportStatusFilter);
      } else if (urlKey) {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
          newParams.set(urlKey, value);
        } else {
          newParams.delete(urlKey);
        }
        setSearchParams(newParams);
      }

      // Reset to first page when filters change
      setCurrentPage(1);
    },
    [searchParams, setSearchParams, setReportStatusAtom, setCurrentPage]
  );

  // Update date filter with Date object
  const setDateFilter = useCallback(
    (key: "dateFrom" | "dateTo", date: Date | undefined) => {
      if (date) {
        const dateString = format(date, "yyyy-MM-dd");
        setFilter(key, dateString);
      } else {
        setFilter(key, "");
      }
    },
    [setFilter]
  );

  // Set report status filter
  const setReportStatus = useCallback(
    (status: ReportStatusFilter) => {
      setReportStatusAtom(status);
      setCurrentPage(1);
    },
    [setReportStatusAtom, setCurrentPage]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({});
    resetFilters();
  }, [setSearchParams, resetFilters]);

  // Filter function that applies all filters to submissions
  const filterSubmissions = useCallback(
    (
      submissions: SubmissionItem[],
      launchCodes: string[] | null,
      reportsData: GeneratedReportsData | undefined
    ): SubmissionItem[] => {
      const individualSet = reportsData?.individual || new Set<number>();
      const referidosSet = reportsData?.referidos || new Set<number>();

      return submissions.filter((submission) => {
        // Filter by launch codes
        if (launchCodes && launchCodes.length > 0) {
          const submissionCode = submission.code;
          if (!submissionCode) return false;

          // Extract base code (remove "R" prefix for referrals)
          const baseCode = submissionCode.startsWith("R")
            ? submissionCode.substring(1)
            : submissionCode;

          if (!launchCodes.includes(baseCode)) {
            return false;
          }
        }

        // Filter by report status (only if reportsData is available)
        if (filters.reportStatus !== "all" && reportsData) {
          const hasIndividual = submission.db_id
            ? individualSet.has(submission.db_id)
            : false;
          const hasReferidos = submission.db_id
            ? referidosSet.has(submission.db_id)
            : false;

          if (filters.reportStatus === "with_individual" && !hasIndividual) {
            return false;
          }
          if (filters.reportStatus === "with_referidos") {
            // Must be principal, have referrals, and have referidos report
            if (
              submission.is_referral ||
              (submission.referral_count || 0) < 1 ||
              !hasReferidos
            ) {
              return false;
            }
          }
          if (
            filters.reportStatus === "without_report" &&
            (hasIndividual || hasReferidos)
          ) {
            return false;
          }
        }

        // Filter by email
        if (filters.email) {
          const email = getSubmissionEmail(submission).toLowerCase();
          if (!email.includes(filters.email.toLowerCase())) {
            return false;
          }
        }

        // Filter by name
        if (filters.name) {
          const name = getSubmissionName(submission).toLowerCase();
          if (!name.includes(filters.name.toLowerCase())) {
            return false;
          }
        }

        // Filter by date range
        if (filters.dateFrom || filters.dateTo) {
          const submissionDate = new Date(submission.created_at);

          if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            if (submissionDate < fromDate) return false;
          }

          if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (submissionDate > toDate) return false;
          }
        }

        return true;
      });
    },
    [filters]
  );

  return {
    filters,
    setFilter,
    setDateFilter,
    setReportStatus,
    clearFilters,
    hasActiveFilters,
    filterSubmissions,
  };
}
